<template>
  <div class="session-request">
    <div v-if="counselor.vacationReturnDate" class="counselor-vacation">
      <p>This counselor is currently on extended leave and will return on  {{ new Date(counselor.vacationReturnDate) | dateMonthDayOnly }}. </p>
      <p>Their response may be delayed during this time.</p>
    </div>
    <div v-show="step === 1">
      <loader v-if="loading" />
      <div v-show="!loading" id="cronofy-date-time-picker" />
    </div>
    <div v-if="step === 2" class="stepTwoHowWillYouPay">
      <div class="col-12">
        <p class="date">
          {{ dateTimeSlot && dateTimeSlot.start | asMoment | format("MMMM Do YYYY, hh:mm A") }}
        </p>
      </div>
      <div v-if="credits && price" class="col-12">
        <div class="form-check custom-control custom-checkbox">
          <input
            id="autoPayWithCredit"
            v-model="autoPayWithCredit"
            name="autoPayWithCredit"
            class="form-check-input"
            type="checkbox"
          >
          <label
            class="form-check-label"
            for="autoPayWithCredit"
          >
            Automatically pay for this session with credits
          </label>
        </div>
      </div>
      <div class="col-12">
        <hr v-if="price" />
        <label v-if="price" class="price-display">
          Session Cost -
          <span v-if="!price">
            FREE
          </span>
          <span v-else-if="autoPayWithCredit">
            PAID WITH CREDIT
          </span>
          <span v-else>
            {{ price/100 | currency }}
          </span>
        </label>
        <p style="font-size: 14px; opacity: 0.6;">
          * Your request will be responded to within the next {{ counselor.vacationReturnDate ? '72' : '24' }}-hours, at which point you will receive an email confirming your session date and time.
        </p>
        <div class="footer-buttons">
          <button
            type="button"
            class="btn btn-secondary"
            @click="goToStepOneSlotPicking()"
          >
            <font-awesome-icon
              v-if="loading"
              :spin="true"
              :icon="['fas', 'spinner']"
              class="icon alt"
            />
            <span v-else>
              <font-awesome-icon
                :icon="['fas', 'chevron-left']"
                class="icon alt"
              />
              Select a different time
            </span>
          </button>
          <button
            type="button"
            class="btn btn-primary"
            @click="requestSession()"
          >
            <font-awesome-icon
              v-if="loading"
              :spin="true"
              :icon="['fas', 'spinner']"
              class="icon alt"
            />
            <span v-else>Request Session</span>
          </button>
        </div>
      </div>
    </div>
    <div v-if="step === 3" class="stepThreeNoSlotsFound">
      <p>
        Uh Oh - looks like this counselor won't be
        available for some time. You are welcome to
        message the counselor about future availability.
        Or to view more counselors on BetterMynd, you
        can review your
        <router-link to="/counselors">
          list of matched counselors
        </router-link>
        to find one with current availability.
      </p>
      <div class="footer-buttons">
        <button
          type="button"
          class="btn btn-secondary"
          @click="emitReturn()"
        >
          <font-awesome-icon
            v-if="loading"
            :spin="true"
            :icon="['fas', 'spinner']"
            class="icon alt"
          />
          <span v-else>
            <font-awesome-icon
              :icon="['fas', 'chevron-left']"
              class="icon alt"
            />
            Return
          </span>
        </button>
        <button
          type="button"
          class="btn btn-primary"
          @click="emitSendAMessage()"
        >
          <font-awesome-icon
            v-if="loading"
            :spin="true"
            :icon="['fas', 'spinner']"
            class="icon alt"
          />
          <span v-else>
            <font-awesome-icon
              :icon="['fa', 'envelope']"
              class="icon alt"
            />
            Send a Message
          </span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import CounselorClient from '@/api/CounselorClient';
import SessionClient from '@/api/SessionClient';
import Loader from '@/components/Loader.vue'
import moment from 'moment';

export default {
  name: 'SessionRequestWorkflow',
  components: {
    Loader,
  },
  props: {
    counselor: {
      type: Object, // { id: Number,  userId: Number }
      required: true,
    }
  },
  data() {
    return {
      counselorClient: new CounselorClient(this.$http),
      sessionClient: new SessionClient(this.$http),
      existing_patient: false,
      price: null,
      dateTimeSlot: null,
      autoPayWithCredit: false,
      userId: 0,
      credits: 0,
      step: 1,
      loading: false,
      cronofyPicker: null,
    }
  },
  async mounted() {
    this.userId = this.$store.getters.currentUser.id;
    this.credits = this.$store.getters.currentUser.student_profile.credits_remaining.individual_session_credits_remaining;
    this.autoPayWithCredit = !!this.credits;

    const pricePromise = this.counselorClient.getSessionPrice(this.counselor.id);
    const initializeCronofyPickerPromise = this.initializeCronofyPicker(initializeCronofyPickerPromise)
    
    try {
      this.loading = true;
      const [priceResponse, cronofyPicker] = await Promise.all([
        pricePromise,
        initializeCronofyPickerPromise]);
      this.price = priceResponse.price;
      this.cronofyPicker = cronofyPicker;
    } catch (ex) {
      this.$emit('onerror', {
        counselor: this.counselor,
        method: 'mounted',
        error: ex,
      });
    } finally {
      this.loading = false;
    }
  },
  methods: {
    async initializeCronofyPicker() {
    const elementTokenResponse = await this.counselorClient.getAvailabilityToken(this.counselor.id);
      const data = {
        element_token: elementTokenResponse.token.token,
        target_id: 'cronofy-date-time-picker',
        availability_query: {
          participants: [
            {
              required: 'all',
              members: elementTokenResponse.token.subjects.map(subject => ({
                sub: subject,
                managed_availability: true,
              }))
            }
          ],
          start_interval: { minutes:30 },
          required_duration: { minutes: 50 },
          query_periods: [
            {
              start: moment().add(25, 'hours').startOf('minute').toISOString(),
              end: moment().add(45, 'days').startOf('minute').toISOString(),
            }
          ]
        },
        config: {
          mode: 'no_confirm',
          month_view_mode: 'single',
        },
        styles: {
          colors: {
            button: '#f5f5f5',
            buttonActive: '#75fffd',
            buttonActiveText: '#000',
            buttonHover: '#75fffd',
            buttonTextHover: '#000',
            buttonConfirm: '#75fffd',
          },
          prefix: 'date-time-picker'
        },
        callback: notification => {
          if (!notification || !notification.notification) return

          const type = notification.notification.type
          if (type === 'error') console.error(notification.notification)
          else if (type === 'warn') console.warn(notification.notification)

          if (type === 'no_slots_found') {
            this.goToStepThreeNoSlotsFound();
          }
          if (type === 'slot_selected') {
            this.dateTimeSlot = notification.notification.slot;
            this.goToStepTwoHowWillYouPay();
          }
        }
      };
      
      return window.CronofyElements.DateTimePicker(data);
    },
    goToStepTwoHowWillYouPay() {
      this.step = 2;
    },
    goToStepOneSlotPicking() {
      this.dateTimeSlot = null;
      this.autoPayWithCredit = !!this.credits;
      this.step = 1;
    },
    goToStepThreeNoSlotsFound() {
      this.step = 3;
    },
    async requestSession() {
      try {
        this.loading = true;
        const res = await this.sessionClient.createRequest(
          this.userId,
          this.counselor.id,
          this.dateTimeSlot.start,
          this.dateTimeSlot.end,
          this.autoPayWithCredit && !!this.credits);

        this.$emit('onrequest', {
          id: res.sessionRequestId,
          counselorId: this.counselor.id,
          counselor_id: this.counselor.id,
          user: { id: this.userId },
          userId: this.userId,
          user_id: this.userId,
          start: this.dateTimeSlot.start,
          start_time: this.dateTimeSlot.start,
          end: this.dateTimeSlot.end,
          end_time: this.dateTimeSlot.end,
        });
      } catch (ex) {
        this.$emit('onerror', {
          counselor: this.counselor,
          method: 'requestSession',
          error: ex,
        });
      } finally {
        this.loading = false;
      }
    },
    emitReturn() {
      this.$emit('onreturn', { })
    },
    emitSendAMessage() {
      this.$emit('onsendamessage', this.counselor)
    },
  },
}
</script>

<style lang="scss">
.session-request {
  .counselor-vacation {
    text-align: center;
    padding: 1rem;
    background-color: $off-green;
    border: 1px solid $green;
    border-radius: 5px;
    font-size: 1.15rem;
    p {
      margin: 0;
    }
  }
  .footer-buttons {
    button {
      width: auto;
      margin-top: 10px;
      margin-bottom: 20px;
    }
    display: flex;
    justify-content: space-between;
    @media (max-width: 500px) {
      flex-direction: column;
      button {
        margin-top: 0px;
        margin-bottom: 10px;
      }
    }
  }
  .stepTwoHowWillYouPay {
    .date {
      font-size: 25px;
    }
    .price-display {
      letter-spacing: .1rem;
      color: #75FFFD;
      font-size: 12px;
      text-transform: uppercase;
    }
  }
  .stepThreeNoSlotsFound {
    padding: 5px 15px 0px 15px;
  }
  .date-time-picker {
    border: 0px;
    background-color: $dark-blue;

    .date-time-picker__calendar-grid--button:disabled {
      color:$light-gray;
      background-color: $blue;
      border:.1em solid $black;
    }
    .date-time-picker__calendar-header-button:disabled {
      .date-time-picker__calendar-header-button--icon {
        fill: $blue;
      }
    }
    .date-time-picker__calendar-grid--button.date-time-picker__calendar-grid--prevnext.date-time-picker__calendar-grid--selected {
      color:$black;
      background-color: $green;
    }
    .date-time-picker__calendar-grid--button.date-time-picker__calendar-grid--prevnext {
      color:$light-gray;
      background-color: $blue;
    }
    .date-time-picker__calendar-grid--button.date-time-picker__calendar-grid--prevnext:disabled {
      color:$light-gray;
      background-color: $blue;
      border:.1em solid $black;
    }
  }
}
</style>
